import { useSite } from "@/context/SiteContext";
import { Eyebrow, Reveal, SectionTitle } from "./Primitives";
import { assetUrl } from "@/lib/api";

export const Ingredients = () => {
  const { content, t } = useSite();
  const list = content.ingredients || [];
  if (!list.length) return null;
  return (
    <section id="ingredients" data-testid="ingredients-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal className="max-w-2xl">
            <Eyebrow>{t("formula_eyebrow")}</Eyebrow>
            <SectionTitle>{t("formula_title_a")} <em className="text-leaf">{t("formula_title_b")}</em></SectionTitle>
          </Reveal>
          {content.ingredients_note && <p className="max-w-sm text-[15px] font-medium leading-relaxed text-sage">{content.ingredients_note}</p>}
        </div>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-3 lg:gap-y-14">
          {list.map((ing, i) => (
            <li key={ing.name} data-testid={`ingredient-card-${i}`}>
              <Reveal delay={(i % 3) * 0.08} className="group">
                <div className={`arch relative aspect-[4/5] overflow-hidden bg-sand ${i % 3 === 1 ? "lg:translate-y-10" : ""}`}>
                  {ing.image && (
                    <img src={assetUrl(ing.image)} alt={`${ing.name} (${ing.latin || "herb"})`} loading="lazy" decoding="async" width="640" height="640"
                      className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
                  )}
                  <span className="absolute left-4 top-6 rounded-full bg-white/85 px-3 py-1 text-sm font-bold text-forest backdrop-blur sm:left-1/2 sm:-translate-x-1/2">0{i + 1}</span>
                </div>
                <div className={i % 3 === 1 ? "lg:translate-y-10" : ""}>
                  <h3 className="mt-5 text-xl font-extrabold tracking-[-0.01em] text-forest sm:text-2xl">{ing.name}</h3>
                  {ing.latin && <p className="text-sm font-semibold text-leaf">{ing.latin}</p>}
                  {ing.text && <p className="mt-2.5 text-[15px] leading-relaxed text-sage sm:text-[17px]">{ing.text}</p>}
                </div>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export const HowToUse = () => {
  const { content, t } = useSite();
  const steps = (content.how_to_use || []).slice(0, 3);
  if (!steps.length) return null;
  return (
    <section id="how-to-use" data-testid="how-to-use-section" className="relative overflow-hidden bg-forest py-20 text-bone grain lg:py-28">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <Eyebrow light>{t("how_eyebrow")}</Eyebrow>
          <SectionTitle light>{t("how_title")}</SectionTitle>
        </Reveal>
        <ol className="grid gap-px overflow-hidden rounded-[28px] bg-white/10 md:grid-cols-3">
          {steps.map((s, i) => (
            <li key={s.title} data-testid={`how-step-${i + 1}`} className="bg-forest">
              <Reveal delay={i * 0.1} className="flex h-full flex-col p-8 sm:p-10">
                <span className="text-6xl font-extrabold leading-none text-gold sm:text-7xl">{i + 1}</span>
                <p className="mt-8 t-label text-white/80">{t("step")} {i + 1}</p>
                <h3 className="t-h3 mt-2">{s.title}</h3>
                <p className="t-body mt-3 text-white/85">{s.text}</p>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
